import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Globe } from "lucide-react";
import { useLanguage } from "@/i18n";
import BrandMark from "@/components/BrandMark";

export const scrollToId = (id) => {
  if (window.__lenis) window.__lenis.scrollTo(id, { offset: -80, duration: 1.4 });
  else document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, id) => {
    e.preventDefault();
    scrollToId(id);
  };

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-[#0F2C50]/95 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => go(e, "#top")}
          data-testid="nav-brand-link"
          className="flex items-center gap-2.5 group"
        >
          <BrandMark className="w-11 h-9 shrink-0 transition-transform duration-500 group-hover:-translate-y-0.5" />
          <span className="flex flex-col">
            <span className="font-serif text-xl lg:text-2xl font-medium tracking-tight text-[#F5F9FA] leading-none">
              Naples <span className="text-[#D2A24C] text-[1.35em] leading-none font-light">&amp;</span> <span className="text-[#5BC0BE]">Beyond Tours</span>
            </span>
            <span className="font-serif font-light text-[11px] lg:text-xs tracking-wide text-[#5BC0BE] mt-1">by Guido Formichella</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10" data-testid="nav-links">
          {[
            ["#tours", t.nav.tours, "nav-tours-link"],
            ["#guida", t.nav.guide, "nav-guide-link"],
            ["#chi-sono", t.nav.about, "nav-about-link"],
            ["#richiedi", t.nav.request, "nav-request-link"],
          ].map(([href, label, testid]) => (
            <a
              key={href}
              href={href}
              data-testid={testid}
              onClick={(e) => go(e, href)}
              className="text-xs font-bold uppercase tracking-[0.18em] text-white/75 hover:text-[#D2A24C] transition-colors duration-300"
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-lang-toggle"
            onClick={() => setLang(lang === "it" ? "en" : "it")}
            className="flex items-center gap-1.5 rounded-full border border-white/25 bg-white/5 px-4 py-2 text-xs font-bold tracking-widest text-white hover:border-[#D2A24C] hover:text-[#D2A24C] transition-colors duration-300"
            aria-label="Switch language"
          >
            <Globe className="w-3.5 h-3.5" strokeWidth={2} />
            {lang === "it" ? "EN" : "IT"}
          </button>
          <button
            data-testid="nav-cta-button"
            onClick={(e) => go(e, "#richiedi")}
            className="hidden sm:inline-flex rounded-full bg-[#C9A227] px-6 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#0F2C50] hover:bg-[#E2B34E] transition-colors duration-300"
          >
            {t.nav.cta}
          </button>
        </div>
      </div>
    </motion.header>
  );
}
